import { apiClient } from '@/lib/api-client';
import type { LoginResponse, TwoFactorStatus, TwoFactorSetupResponse } from '@just-frotas/shared';

export const twoFactorService = {
  status(): Promise<TwoFactorStatus> {
    return apiClient.request<TwoFactorStatus>('/auth/2fa/status');
  },

  setup(): Promise<TwoFactorSetupResponse> {
    return apiClient.request<TwoFactorSetupResponse>('/auth/2fa/setup', { method: 'POST' });
  },

  confirm(code: string): Promise<{ ok: boolean }> {
    return apiClient.request('/auth/2fa/confirm', { method: 'POST', body: { code } });
  },

  disable(senha: string, code: string): Promise<{ ok: boolean }> {
    return apiClient.request('/auth/2fa/disable', { method: 'POST', body: { senha, code } });
  },

  // Verifica codigo TOTP durante o fluxo de login. Backend devolve o
  // mesmo shape de LoginResponse, completando o login.
  verifyLogin(
    challengeToken: string,
    code: string,
    options: { trustDevice?: boolean } = {},
  ): Promise<LoginResponse> {
    const body: Record<string, unknown> = { challengeToken, code };
    if (options.trustDevice) body.trustDevice = true;
    return apiClient.request<LoginResponse>('/auth/2fa/verify', {
      method: 'POST',
      body,
      auth: false,
    });
  },

  listTrustedDevices(): Promise<{ devices: TrustedDevice[] }> {
    return apiClient.request('/auth/2fa/trusted-devices');
  },

  revokeTrustedDevice(id: number): Promise<unknown> {
    return apiClient.request(`/auth/2fa/trusted-devices/${id}`, { method: 'DELETE' });
  },
};

// Shape REAL retornado por GET /v1/auth/2fa/trusted-devices
// (probe 2026-04-28). Backend manda UA bruto + trustedAt; frontend
// parseia browser/OS via parseUserAgent abaixo.
export interface TrustedDevice {
  id: number;
  deviceFingerprint: string;
  ip: string | null;
  userAgent: string | null;
  label: string | null;
  trustedAt: string; // ISO 8601 - momento em que foi marcado confiavel
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  currentDevice: boolean; // true quando o fingerprint do request bate
}

// Heuristica simples pra extrair browser + OS de um userAgent string.
// Cobre os casos modernos (Chrome/Edge/Firefox/Safari + Mac/Win/Linux/iOS/Android)
// sem dependencia de parser pesado. Quando nao reconhece, devolve string
// truncada do proprio UA.
export function parseUserAgent(ua: string | null): {
  browser: string;
  os: string | null;
} {
  if (!ua) return { browser: 'Dispositivo desconhecido', os: null };
  // Browser detection (ordem importa: Edg vem ANTES de Chrome).
  let browser = 'Navegador';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera\//.test(ua)) browser = 'Opera';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua) && /Version\//.test(ua)) browser = 'Safari';
  else if (/curl\//.test(ua)) browser = 'curl';
  // OS detection
  let os: string | null = null;
  if (/Windows NT 10/.test(ua)) os = 'Windows 10/11';
  else if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Mac OS X ([\d_]+)/.test(ua)) {
    const m = ua.match(/Mac OS X ([\d_]+)/);
    os = m ? `macOS ${m[1].replace(/_/g, '.')}` : 'macOS';
  } else if (/Android ([\d.]+)/.test(ua)) {
    const m = ua.match(/Android ([\d.]+)/);
    os = m ? `Android ${m[1]}` : 'Android';
  } else if (/iPhone OS ([\d_]+)/.test(ua)) {
    const m = ua.match(/iPhone OS ([\d_]+)/);
    os = m ? `iOS ${m[1].replace(/_/g, '.')}` : 'iOS';
  } else if (/iPad/.test(ua)) {
    os = 'iPadOS';
  } else if (/Linux/.test(ua)) {
    os = 'Linux';
  }
  return { browser, os };
}
